"use client";

import { useLocation } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/utils";

const x = [
  {
    name: "Document Generator",
    id: "document-generator",
    url: "#",
    visualName: "Document Generator",
  },
  {
    name: "Data Extraction",
    id: "scanner",
    url: "#",
    visualName: "Scanner",
  },
  {
    name: "chat",
    id: "chat",
    url: "#",
    visualName: "Chat",
  },
  {
    name: "call-sms",
    id: "call-sms",
    url: "#",
    visualName: "Call/SMS",
  },
  {
    name: "My Flows",
    id: "my-flows",
    url: "#",
    visualName: "My Flows",
  },
  {
    name: "Process Prior Auths",
    id: "prior-auth",
    url: "#",
    visualName: "Prior Authorization",
  },
  {
    name: "Failure Management",
    id: "failure-management",
    url: "#",
    visualName: "Failure Management",
  },
  {
    name: "Medical Reconciliation",
    id: "med-rec",
    url: "#",
    visualName: "Medical Reconciliation",
  },
  {
    name: "Application Writing",
    id: "desktop-write",
    url: "#",
    visualName: "Application Writing",
  },
  {
    name: "Formulary",
    id: "formulary",
    url: "#",
    visualName: "Formulary",
  },
  {
    name: "CoverMyMeds",
    id: "cmm",
    url: "#",
    visualName: "CoverMyMeds",
  },
  {
    name: "Scheduler",
    id: "scheduler",
    url: "#",
    visualName: "Scheduler",
  },
  {
    name: "Drug Therapy Problems",
    id: "med-review",
    url: "#",
    visualName: "Drug Therapy Problems",
  },
  {
    name: "Operations Dashboard",
    id: "operations-dashboard",
    url: "#",
    visualName: "Operations Dashboard",
  },
];

export function SiteHeader({ className }: { className?: string }) {
  const location = useLocation();
  const pathname = location.pathname;

  // Derive activePage from pathname
  const deriveActivePageFromPathname = () => {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      return segments[0]; // e.g., 'email', 'call-sms', 'browser', 'my-flows'
    }
    return "chat"; // Defaulting to my-flows for now if path is '/'
  };

  const activePage = deriveActivePageFromPathname();

  // Function to determine the parent category based on the active page
  const getCategoryInfo = () => {
    // Tools pages
    const toolsPages = [
      "chat",
      "mail",
      "email",
      "scanner",
      "data-extraction",
      "browser",
      "document-generator",
      "call-sms",
      "discrepancy-analysis",
      "formulary",
      "med-review",
      "scheduler",
      "cmm",
    ];
    if (toolsPages.includes(activePage)) {
      return {
        category: "Tools",
        categoryLink: "#tools",
      };
    }

    // Workflow pages (now for FlowTable)
    const workflowPages = [
      "my-flows",
      "med-rec",
      "prior-auth",
      "failure-management",
      "desktop-write",
    ];
    if (workflowPages.includes(activePage)) {
      return {
        category: "Workflows", // Or "Flows" or "Tasks" depending on desired breadcrumb
        categoryLink: "/my-flows",
      };
    }

    // Tasks pages (Removed dashboard, updated failure-management link)
    const tasksPages = ["failure-management"];
    if (tasksPages.includes(activePage)) {
      return {
        category: "Tasks",
        categoryLink: "/failure-management",
      };
    }

    // Analytics pages
    const analyticsPages = ["operations-dashboard", "dashboard"];
    if (analyticsPages.includes(activePage)) {
      return {
        category: "Analytics",
        categoryLink: "/operations-dashboard",
      };
    }

    // Settings pages
    const settingsPages = [
      "integrations",
      "team",
      "account",
      "help-center",
      "profile",
    ];
    if (settingsPages.includes(activePage)) {
      return {
        category: "Settings",
        categoryLink: "#settings",
      };
    }

    // Default fallback
    return {
      category: "",
      categoryLink: "#",
    };
  };

  const { category, categoryLink } = getCategoryInfo();

  // Format the page title
  const getPageTitle = () => {
    if (!activePage) return "Home";
    const page = x.find((p) => p.id === activePage);
    return (
      page?.visualName ||
      activePage.charAt(0).toUpperCase() +
        activePage.slice(1).replace(/-/g, " ")
    );
  };

  return (
    <header
      className={cn(
        "group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex shrink-0 items-center gap-2 transition-[width,height] ease-linear z-[1] sticky top-0 h-16 bg-white dark:bg-black border-b border-slate-300 dark:border-neutral-800",
        className,
      )}
    >
      <div className="flex w-full items-center px-4 lg:px-6">
        <div className="flex items-center gap-1 lg:gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />

          <Breadcrumb>
            <BreadcrumbList>
              {category ? (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink href={categoryLink}>
                      {category}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{getPageTitle()}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              ) : (
                <BreadcrumbItem>
                  <BreadcrumbPage>{getPageTitle()}</BreadcrumbPage>
                </BreadcrumbItem>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
    </header>
  );
}
